<!-- 
Dialog for copying overrides from the previous year's levy collection.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog lg">
    <v-card>
      <v-toolbar color="secondary" flat dark>
        <v-toolbar-title>Copy overrides from last year</v-toolbar-title>
      </v-toolbar>
      <our-busy-indicator :show="processing"/>
      <our-loading-indicator v-if="loading"/>
      <v-alert :value="noCandidates" type="info">
        <p>
          There are no overrides available to be copied from last year's levy collection. Possible reasons for this include:
        </p>
        <ul>
          <li>they have already been copied; or</li>
          <li>this is the first levy collection; or</li>
          <li>there are no levy calculation adjustment overrides that exempt a provider from some or all of the levy components.</li>
        </ul>
      </v-alert>
      <v-card-text v-if="!noCandidates">
        <div v-if="state.loaded && !noCandidates">
          <p>
            Here are the overrides that are able to be copied from last year's levy collection. 
            Only overrides that exempt a provider from some or all of the levy are listed. 
          </p>
          <p>
            Select the overrides you want to copy:
          </p>
        </div>
        <v-data-table 
          v-if="state.loaded && !noCandidates" 
          v-model="selectedOverrides"
          :headers="headers" 
          :items="overrides" 
          :select-all="!processing" 
          item-key="id" 
          style="max-height:300px; overflow-y:auto" 
          hide-actions 
          must-sort
        >
          <template v-slot:items="props">
            <tr :ref="`row${props.item.id}`" :class="processing ? 'noselect' : ''" @click="toggle(props.item)">
              <td>
                <v-checkbox :disabled="processing" :value="props.selected" color="primary" hide-details/>
              </td>
              <td>{{ props.item.providerName }}</td>
              <td>{{ props.item.description }}</td>
              <td>{{ props.item.explanation }}</td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn v-if="noCandidates" flat @click="hide">Close</v-btn>
        <v-btn v-if="!noCandidates" :disabled="busy" flat @click="hide">Cancel</v-btn>
        <v-btn 
          v-if="!noCandidates" 
          :disabled="busy || noOverridesSelected" 
          flat color="success" 
          @click="save"
        >
          {{ buttonCaption }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <our-validation-dialog v-model="showSpecifiedValidationDialog" :specified-error="specifiedValidationError" />
  </v-dialog>
</template>

<script>
import * as action from "@/store/levy-collection-overrides/action-types";

export default {
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    levyCollectionId: {
      validator: prop => typeof prop === "number" || prop === null,
      required: true
    }
  },
  data() {
    return {
      showSpecifiedValidationDialog: false,
      specifiedValidationError: null,
      selectedOverrides: []
    };
  },
  computed: {
    headers() {
      const h = [];
      if (this.processing) {
        h.push({ sortable: false, width: 100 });
      }
      h.push({ text: "Provider", sortable: true, value: "providerName" });
      h.push({ text: "Description", sortable: false });
      h.push({ text: "Explanation", sortable: false });
      return h;
    },
    noOverridesSelected() {
      return this.selectedOverrides.length == 0;
    },
    noCandidates() {
      return this.state.loaded && this.overrides.length == 0;
    },
    buttonCaption() {
      if (this.noOverridesSelected) {
        return "Copy";
      } else {
        return `Copy ${this.pluralise(this.selectedOverrides.length)}`;
      }
    },
    state() {
      return this.$store.state.levyCollectionOverrides.candidatesForCopying;
    },
    overrides() {
      return this.state.data;
    },
    busy() {
      return this.loading || this.processing;
    },
    loading() {
      return this.state.loading;
    },
    processing() {
      return this.$store.state.levyCollectionOverrides.copy.processing;
    },
    selectedOverrideIds() {
      return this.selectedOverrides.map(o => o.id);
    }
  },
  watch: {
    show(nowShowing) {
      if (nowShowing) {
        this.selectedOverrides = [];
        this.reload();
      }
    }
  },
  methods: {
    reload() {
      const needsReloaded =
        (!this.state.loaded && !this.state.loading) || this.levyCollectionId != this.state.levyCollectionId;
      if (needsReloaded) {
        this.$store.dispatch(action.GET_CANDIDATES_FOR_COPYING, this.levyCollectionId);
      }
    },
    hide() {
      this.$emit("update:show", false);
    },
    pluralise(count) {
      if (count == 1) {
        return "1 override";
      } else {
        return `${count} overrides`;
      }
    },
    toggle(override) {
      if (!this.processing) {
        const index = this.selectedOverrides.indexOf(override);
        if (index !== -1) {
          this.selectedOverrides.splice(index, 1);
        } else {
          this.selectedOverrides.push(override);
        }
      }
    },
    async save() {
      if (this.noOverridesSelected) {
        this.specifiedValidationError = "Select the overrides you want to copy.";
        this.showSpecifiedValidationDialog = true;
      } else {
        await this.$store.dispatch(action.COPY, {
          levyCollectionId: this.levyCollectionId,
          overrideIds: this.selectedOverrideIds
        });
        this.hide();
      }
    }
  }
};
</script>
